// Dimension labels for anything being resized: the same numbers, unit
// and calibration the Measure tool reports, shown against the selection
// itself so you don't have to reach for a different tool to find out how
// big the thing you are dragging has become.
//
// Two renderers because the overlays are built two ways. Annotations
// live in one SVG per page (page-point user space, so everything scales
// with zoom and constants are divided by `scale` to stay a fixed size on
// screen); the content frames — images, colour fills, stamps — are
// positioned HTML. The numbers and the styling match; only the substrate
// differs.
import { useFormatLength } from "../lib/measure-format.ts";

/** Measure-tool orange, so a dimension always reads as a measurement
 *  wherever it appears. */
const INK = "#e8590c";

/** Roughly how wide the label box needs to be. SVG can't measure text
 *  without a layout pass, and a background that hugs the glyphs matters
 *  less than one that never clips them — so this errs generous. */
function boxWidth(label: string): number {
  return label.length * 6.4 + 10;
}

function SvgLabel({
  cx,
  cy,
  label,
  scale,
}: {
  cx: number;
  cy: number;
  label: string;
  scale: number;
}) {
  const w = boxWidth(label) / scale;
  const h = 16 / scale;
  return (
    <g pointerEvents="none">
      <rect
        x={cx - w / 2}
        y={cy - h / 2}
        width={w}
        height={h}
        rx={3 / scale}
        fill="var(--color-bg)"
        stroke={INK}
        strokeWidth={1 / scale}
      />
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fill={INK}
        fontSize={11 / scale}
        fontWeight={500}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {label}
      </text>
    </g>
  );
}

/** Width along the top edge and height along the left edge of a box, in
 *  page-point space. Each label sits just OUTSIDE its edge so it doesn't
 *  cover the content being sized — unless there is no room out there, in
 *  which case it flips inside. A box drawn against the top of the page is
 *  a normal thing to draw, and a measurement clipped off the edge of the
 *  page is no measurement at all. */
export function SvgBoxDims({
  x,
  y,
  width,
  height,
  scale,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}) {
  const fmt = useFormatLength();
  const gap = 12 / scale;
  const halfH = 8 / scale;
  const wLabel = fmt(width);
  const hLabel = fmt(height);
  const halfW = boxWidth(hLabel) / (2 * scale);
  // The SVG's user space starts at the page's top-left, so "off the page"
  // is simply a negative coordinate.
  const wy = y - gap - halfH < 0 ? y + gap : y - gap;
  const hx = x - gap - halfW < 0 ? x + gap + halfW : x - gap - halfW;
  return (
    <>
      {width > 0 && <SvgLabel cx={x + width / 2} cy={wy} label={wLabel} scale={scale} />}
      {height > 0 && <SvgLabel cx={hx} cy={y + height / 2} label={hLabel} scale={scale} />}
    </>
  );
}

/** One length along a line — the Measure tool's own reading, for the
 *  shapes where a diagonal is the dimension that matters. */
export function SvgLineDim({
  x1,
  y1,
  x2,
  y2,
  scale,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  scale: number;
}) {
  const fmt = useFormatLength();
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len <= 0) return null;
  return (
    <SvgLabel
      cx={(x1 + x2) / 2}
      cy={(y1 + y2) / 2 - 12 / scale}
      label={fmt(len)}
      scale={scale}
    />
  );
}

/** The HTML equivalent, for the content frames. Positioned against the
 *  frame it is a child of, so the caller passes sizes in POINTS and this
 *  handles the zoom.
 *
 *  `pointer-events: none` on both labels matters more than it looks: the
 *  frames they sit in are draggable, and a label that swallowed a press
 *  would make the top-left corner of every selection dead. */
export function HtmlBoxDims({
  width,
  height,
  scale,
  originX = Infinity,
  originY = Infinity,
}: {
  width: number;
  height: number;
  scale: number;
  /** The labelled frame's own position on the page, in points — it is
   *  what decides whether there is room outside its top and left edges.
   *  Left off, the labels stay outside (the pre-clamp behaviour). */
  originX?: number;
  originY?: number;
}) {
  const fmt = useFormatLength();
  const cls =
    "pointer-events-none absolute z-40 whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-medium tabular-nums shadow-sm";
  const style = { borderColor: INK, color: INK, background: "var(--color-bg)" };
  // Would the label fit between this frame's edge and the page's?
  const roomAbove = originY * scale > 24;
  const roomLeft = originX * scale > boxWidth(fmt(height)) + 8;
  return (
    <>
      <div
        className={`${cls} -translate-x-1/2 ${roomAbove ? "-translate-y-full" : ""}`}
        style={{ ...style, left: (width * scale) / 2, top: roomAbove ? -6 : 6 }}
      >
        {fmt(width)}
      </div>
      <div
        className={`${cls} -translate-y-1/2 ${roomLeft ? "-translate-x-full" : ""}`}
        style={{ ...style, left: roomLeft ? -6 : 6, top: (height * scale) / 2 }}
      >
        {fmt(height)}
      </div>
    </>
  );
}
